import { stylesTemplate } from "./utilities/styles.js";
import { createLogger } from "./utilities/debug.js";
import { defineObservedAttributes, resolveAttributeConfigs } from "./attributes/config.js";
import { ChangeDetector } from "./attributes/change-detection.js";
import { AttributeTypeError, AttributeValidationError } from "./attributes/errors.js";
import { UpdateScheduler } from "./model/update-cycle.js";
import { registerComponent } from "./model/registration.js";
import { getComponentConstructor, isComponentRegistered, registerComponents, whenComponentDefined } from "./model/registration.js";
import { validateAllSlots, validateSlotElements } from "./slots/slot-validation.js";
import { createSlotchangeHandler } from "./slots/slot-events.js";
import { querySlottedElements, querySlottedElement, hasSlottedContent } from "./slots/slot-query.js";
import { IntersectionController, MediaQueryController } from "./model/controllers.js";
import { BatchRegistrationError, ComponentRegistrationError } from "./model/errors.js";
var __create = Object.create;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : /* @__PURE__ */ Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var __decoratorStart = (base) => [, , , __create(base?.[__knownSymbol("metadata")] ?? null)];
var __decoratorStrings = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError("Function expected") : fn;
var __decoratorContext = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError("Already initialized") : fns.push(__expectFn(fn || null)) });
var __decoratorMetadata = (array, target) => __defNormalProp(target, __knownSymbol("metadata"), array[3]);
var __runInitializers = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self);
  return value;
};
var __decorateElement = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, k = flags & 7, p = false;
  var j = 0;
  var extraInitializers = array[j] || (array[j] = []);
  var desc = k && (target = target.prototype, k < 5 && (k > 3 || !p) && __getOwnPropDesc(target, name));
  __name(target, name);
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext(k, name, done = {}, array[3], extraInitializers);
    it = (0, decorators[i])(target, ctx), done._ = 1;
    __expectFn(it) && (target = it);
  }
  return __decoratorMetadata(array, target), desc && __defProp(target, name, desc), p ? k ^ 4 ? extra : desc : target;
};
const ComponentConfig = (config) => {
  return function(constructor) {
    constructor.componentConfig = config;
    return constructor;
  };
};
class BaseComponent extends HTMLElement {
  constructor() {
    super();
    this.elementRef = null;
    this._resolvedReactiveAttrs = [];
    this._changeDetector = new ChangeDetector();
    this._reactiveValues = /* @__PURE__ */ new Map();
    this._isReflecting = false;
    this._hasConnected = false;
    this._hasFirstUpdated = false;
    this._changedProperties = /* @__PURE__ */ new Map();
    this._isUpdating = false;
    this._pendingPreUpgrade = null;
    this._slotCleanups = [];
    this._controllers = /* @__PURE__ */ new Set();
    const constructor = this.constructor;
    this.config = constructor.componentConfig;
    if (!this.config) {
      throw new Error(
        "Component config not found. Did you forget to use the @Component decorator?"
      );
    }
    this._logger = createLogger(this.config.tagName);
    this._updateScheduler = new UpdateScheduler(() => this._performUpdate());
    this.shadow = this.attachShadow({ mode: "open" });
    this.validateConfig();
    this.setupReactiveAttributes();
  }
  static get observedAttributes() {
    const handlerNames = this.componentConfig?.attributes?.map((attr) => attr.name) || [];
    const reactiveNames = this.componentConfig?.reactiveAttributes ? defineObservedAttributes(this.componentConfig.reactiveAttributes) : [];
    return [.../* @__PURE__ */ new Set([...handlerNames, ...reactiveNames])];
  }
  connectedCallback() {
    try {
      this.applyPreUpgradeProperties();
      if (!this._hasConnected) {
        this._hasConnected = true;
        this.executeFirstConnected();
      }
      this.initializeComponent();
    } catch (error) {
      this.handleError("Failed to initialize component", error);
    }
  }
  disconnectedCallback() {
    this.cleanup();
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    try {
      this.handleAttributeChange(name, oldValue, newValue);
    } catch (error) {
      this.handleError(`Failed to handle attribute change: ${name}`, error);
    }
  }
  initializeComponent() {
    try {
      this.validateSlots();
      if (!this._hasFirstUpdated) {
        this._hasFirstUpdated = true;
        this.executeWillFirstUpdate();
      }
      const componentRef = this.config.createComponent(this);
      if (!componentRef || !componentRef.element) {
        throw new Error(
          "Component creation failed: Invalid component reference"
        );
      }
      this.elementRef = componentRef;
      this.setupShadowDom(componentRef);
      this.setupSlotObservers();
      this.notifyControllers("hostConnected");
      if (this._changedProperties.size > 0) {
        this._updateScheduler.schedule();
      }
      this.afterInit();
    } catch (error) {
      this.handleError("Failed to initialize component", error);
    }
  }
  setupShadowDom(component) {
    this.shadow.appendChild(
      stylesTemplate({ styles: component.styles || "" }).content.cloneNode(true)
    );
    this.shadow.appendChild(component.element);
  }
  validateConfig() {
    const requiredFields = [
      "tagName",
      "createComponent"
    ];
    requiredFields.forEach((field) => {
      if (!this.config[field]) {
        throw new Error(`Missing required config field: ${field}`);
      }
    });
    if (this.config.attributes) {
      this.config.attributes.forEach((attr) => {
        if (!attr.name || !attr.handler) {
          throw new Error(
            "Invalid attribute config: requires name and handler"
          );
        }
      });
    }
  }
  async afterInit() {
    if (!this.elementRef) {
      this.handleError("Cannot initialize - missing element reference", null);
      return;
    }
    await this.executeLifecycleCallbacks();
    this.dispatchComponentEvent("umdComponent:ready", {
      shadowRoot: this.shadow,
      ref: this.elementRef
    });
  }
  handleAttributeChange(name, oldValue, newValue) {
    if (this.elementRef) {
      const handler = this.config.attributes?.find(
        (attr) => attr.name === name
      )?.handler;
      if (handler) {
        handler(
          this.elementRef,
          oldValue,
          newValue
        );
      }
    }
    if (!this._isReflecting) {
      const resolved = this._resolvedReactiveAttrs.find(
        (r) => r.attributeName === name
      );
      if (resolved) {
        let converted;
        try {
          converted = resolved.converter.fromAttribute(newValue, name);
        } catch (err) {
          throw new AttributeTypeError(
            name,
            resolved.type ?? "unknown",
            newValue,
            this.config.tagName
          );
        }
        const value = converted !== void 0 ? converted : resolved.defaultValue;
        if (value !== void 0 && resolved.validate) {
          const error = resolved.validate(value);
          if (error) {
            throw new AttributeValidationError(name, error, this.config.tagName);
          }
        }
        const oldValue2 = this._reactiveValues.get(resolved.propertyName);
        const equalityOverride = resolved.hasChanged ? (prev, next) => !resolved.hasChanged(next, prev) : void 0;
        const changed = this._changeDetector.set(resolved.propertyName, value, equalityOverride);
        this._reactiveValues.set(resolved.propertyName, value);
        if (changed) {
          if (resolved.onChange) {
            resolved.onChange(this, value, oldValue2);
          }
          this.requestUpdate(resolved.propertyName, oldValue2);
        }
      }
    }
  }
  /**
   * Capture a property value set on the instance before upgrade.
   * Deletes the instance property so the class accessor takes precedence,
   * then returns the captured value for re-application.
   */
  capturePreUpgradeProperty(propertyName) {
    if (this.hasOwnProperty(propertyName)) {
      const value = this[propertyName];
      delete this[propertyName];
      return { value, captured: true };
    }
    return { value: void 0, captured: false };
  }
  /**
   * Apply pre-upgrade property values through reactive setters.
   * Called in connectedCallback after all attributeChangedCallback
   * calls have completed during upgrade.
   */
  applyPreUpgradeProperties() {
    if (!this._pendingPreUpgrade) return;
    const pending = this._pendingPreUpgrade;
    this._pendingPreUpgrade = null;
    for (const [propertyName, value] of pending) {
      this[propertyName] = value;
    }
  }
  setupReactiveAttributes() {
    if (!this.config.reactiveAttributes) return;
    this._resolvedReactiveAttrs = resolveAttributeConfigs(
      this.config.reactiveAttributes
    );
    for (const resolved of this._resolvedReactiveAttrs) {
      const { propertyName, defaultValue } = resolved;
      const preUpgrade = this.capturePreUpgradeProperty(propertyName);
      if (resolved.attributeName !== false) {
        const raw = this.getAttribute(resolved.attributeName);
        if (raw !== null) {
          const converted = resolved.converter.fromAttribute(
            raw,
            resolved.attributeName
          );
          this._reactiveValues.set(
            propertyName,
            converted !== void 0 ? converted : defaultValue
          );
        } else if (defaultValue !== void 0) {
          this._reactiveValues.set(propertyName, defaultValue);
        }
      } else if (defaultValue !== void 0) {
        this._reactiveValues.set(propertyName, defaultValue);
      }
      Object.defineProperty(this, propertyName, {
        get: () => this._reactiveValues.get(propertyName),
        set: (value) => {
          if (value !== void 0 && resolved.validate) {
            const error = resolved.validate(value);
            if (error) {
              const attrName = resolved.attributeName !== false ? resolved.attributeName : propertyName;
              throw new AttributeValidationError(attrName, error, this.config.tagName);
            }
          }
          const oldValue = this._reactiveValues.get(propertyName);
          const equalityOverride = resolved.hasChanged ? (prev, next) => !resolved.hasChanged(next, prev) : void 0;
          const changed = this._changeDetector.set(propertyName, value, equalityOverride);
          if (!changed) return;
          this._reactiveValues.set(propertyName, value);
          if (resolved.reflect && resolved.attributeName !== false) {
            this._isReflecting = true;
            try {
              const attrValue = resolved.converter.toAttribute(value);
              if (attrValue === null) {
                this.removeAttribute(resolved.attributeName);
              } else {
                this.setAttribute(resolved.attributeName, attrValue);
              }
            } finally {
              this._isReflecting = false;
            }
          }
          if (resolved.onChange) {
            resolved.onChange(this, value, oldValue);
          }
          this.requestUpdate(propertyName, oldValue);
        },
        configurable: true,
        enumerable: true
      });
      if (preUpgrade.captured) {
        if (!this._pendingPreUpgrade) {
          this._pendingPreUpgrade = /* @__PURE__ */ new Map();
        }
        this._pendingPreUpgrade.set(propertyName, preUpgrade.value);
      }
    }
  }
  addController(controller) {
    this._controllers.add(controller);
    if (this.isConnected && this.elementRef) {
      controller.hostConnected?.();
    }
  }
  removeController(controller) {
    this._controllers.delete(controller);
  }
  requestUpdate(name, oldValue) {
    if (name !== void 0 && !this._changedProperties.has(name)) {
      this._changedProperties.set(name, oldValue);
    }
    if (!this._hasConnected) return;
    this._updateScheduler.schedule();
  }
  get updateComplete() {
    return this._updateScheduler.updateComplete;
  }
  willUpdate(_changedProperties) {
    this.config.willUpdate?.(this, _changedProperties);
  }
  update(_changedProperties) {
    this.config.update?.(this, _changedProperties);
  }
  updated(_changedProperties) {
    this.config.updated?.(this, _changedProperties);
  }
  _performUpdate() {
    if (this._isUpdating) return true;
    this._isUpdating = true;
    const changedProperties = this._changedProperties;
    this._changedProperties = /* @__PURE__ */ new Map();
    try {
      for (const controller of this._controllers) {
        controller.hostUpdate?.();
      }
      this.willUpdate(changedProperties);
      this.update(changedProperties);
      this.updated(changedProperties);
      for (const controller of this._controllers) {
        controller.hostUpdated?.();
      }
      return true;
    } catch (error) {
      this.handleError("Error during update cycle", error);
      return false;
    } finally {
      this._isUpdating = false;
    }
  }
  notifyControllers(method) {
    for (const controller of this._controllers) {
      try {
        controller[method]?.();
      } catch (error) {
        this.handleError(`Controller ${method} failed`, error);
      }
    }
  }
  querySlot(slotName, selector) {
    return querySlottedElements(this, slotName, { selector, flatten: true });
  }
  querySlotElement(slotName, selector) {
    return querySlottedElement(this, slotName, selector);
  }
  hasSlotContent(slotName) {
    return hasSlottedContent(this, slotName);
  }
  cleanup() {
    this.notifyControllers("hostDisconnected");
    for (const fn of this._slotCleanups) fn();
    this._slotCleanups = [];
    this.elementRef = null;
  }
  /**
   * Helper method to dispatch component events with consistent structure
   */
  dispatchComponentEvent(eventName, detail) {
    const fullDetail = {
      ...detail,
      tagName: this.config.tagName,
      element: this,
      timestamp: Date.now()
    };
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail: fullDetail,
        bubbles: true,
        composed: true
      })
    );
  }
  handleError(message, error) {
    this._logger.error(message, error);
    const errorType = this.elementRef ? "runtime" : message.toLowerCase().includes("validation") ? "validation" : "initialization";
    this.dispatchComponentEvent("umdComponent:error", {
      type: errorType,
      message,
      details: error
    });
  }
  validateSlots() {
    if (!this.config.slots) return;
    const result = validateAllSlots(this, this.config.slots);
    this.reportSlotErrors(result);
  }
  reportSlotErrors(result) {
    if (result.isValid) return;
    this.handleError("Slot validation failed", result.errors);
    if (result.errors.length === 1) {
      this._logger.warn(result.errors[0].message);
    } else {
      this._logger.group(`${result.errors.length} slot validation warnings`);
      for (const error of result.errors) {
        this._logger.warn(error.message);
      }
      this._logger.groupEnd();
    }
  }
  setupSlotObservers() {
    if (!this.config.slots) return;
    const configKeys = Object.keys(this.config.slots);
    const kebabToConfig = /* @__PURE__ */ new Map();
    for (const key of configKeys) {
      const kebab = key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`).replace(/^-/, "");
      kebabToConfig.set(kebab, { key, config: this.config.slots[key] });
    }
    const handler = createSlotchangeHandler(
      this,
      (event) => {
        const slotName = event.slotName ?? "";
        const entry = kebabToConfig.get(slotName);
        if (!entry) return;
        const result = validateSlotElements(entry.key, event.elements, entry.config);
        this.reportSlotErrors(result);
      },
      { flatten: true }
    );
    handler.connect();
    this._slotCleanups.push(() => handler.disconnect());
  }
  async executeLifecycleCallbacks() {
    const lifecycleMethods = [
      "beforeConnect",
      "afterConnect",
      "onReady"
    ];
    for (const method of lifecycleMethods) {
      await this.executeCallback(method);
    }
  }
  async executeCallback(name) {
    const callback = this.config[name];
    if (!callback) {
      return;
    }
    try {
      await Promise.resolve(callback(this.elementRef, this.shadow));
    } catch (error) {
      this.handleError(`Failed to execute ${name} callback`, error);
    }
  }
  getRef() {
    return this.elementRef;
  }
  getShadowRoot() {
    return this.shadow;
  }
  executeFirstConnected() {
    try {
      if (this.config.firstConnected) {
        this.config.firstConnected(this, this.shadow);
      }
      this.firstConnected();
    } catch (error) {
      this.handleError("Failed to execute firstConnected hook", error);
    }
  }
  firstConnected() {
  }
  executeWillFirstUpdate() {
    try {
      if (this.config.willFirstUpdate) {
        this.config.willFirstUpdate(this, this.shadow);
      }
      this.willFirstUpdate();
    } catch (error) {
      this.handleError("Failed to execute willFirstUpdate hook", error);
    }
  }
  willFirstUpdate() {
  }
}
const createCustomElement = (config) => {
  var _Component_decorators, _init, _a;
  _Component_decorators = [ComponentConfig(config)];
  class Component extends (_a = BaseComponent) {
    constructor() {
      super();
    }
  }
  _init = __decoratorStart(_a);
  Component = __decorateElement(_init, 0, "Component", _Component_decorators, Component);
  __runInitializers(_init, 1, Component);
  return Component;
};
const defineComponent = (config, options) => {
  let element = null;
  return () => {
    if (!element) {
      element = createCustomElement(config);
    }
    registerComponent(config.tagName, element, options);
  };
};
export {
  BaseComponent,
  BatchRegistrationError,
  ComponentRegistrationError,
  IntersectionController,
  MediaQueryController,
  createCustomElement,
  defineComponent,
  getComponentConstructor,
  isComponentRegistered,
  registerComponent,
  registerComponents,
  whenComponentDefined
};
//# sourceMappingURL=model.js.map
